(() => {
  if (window.MSEditorial) return;
  const disposers = new WeakMap();
  const motion = () => window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  function initialiseMission(root) {
    const hero = root.querySelector('.mission-hero');
    const art = root.querySelector('.mission-hero-art');
    if (!hero || !art) return () => {};
    const reset = () => {
      art.style.setProperty('--mission-x', '0px');
      art.style.setProperty('--mission-y', '0px');
    };
    const move = event => {
      if (motion() || event.pointerType !== 'mouse') return;
      const box = hero.getBoundingClientRect();
      art.style.setProperty('--mission-x', ((event.clientX - box.left - box.width / 2) / box.width * 18) + 'px');
      art.style.setProperty('--mission-y', ((event.clientY - box.top - box.height / 2) / box.height * 12) + 'px');
    };
    hero.addEventListener('pointermove', move);
    hero.addEventListener('pointerleave', reset);
    return () => {
      hero.removeEventListener('pointermove', move);
      hero.removeEventListener('pointerleave', reset);
      reset();
    };
  }

  function initialiseMystery(root) {
    const range = root.querySelector('[data-attention-range]');
    const image = root.querySelector('.attention-image img');
    const shutter = root.querySelector('.attention-shutter');
    const space = root.querySelector('[data-breath-space]');
    const button = space?.querySelector('[data-breath-toggle]');
    const phrase = space?.querySelector('p');
    let timer = null;
    let breathing = false;
    const perspective = () => {
      const value = Math.max(0, Math.min(100, Number(range.value) || 0));
      image.style.filter = 'blur(' + (100 - value) / 12 + 'px)';
      shutter.style.width = (100 - value) * .35 + '%';
    };
    const stop = () => {
      if (timer !== null) window.clearInterval(timer);
      timer = null;
      breathing = false;
      space.classList.remove('is-breathing');
      phrase.textContent = space.dataset.breathIdle;
      button.querySelector('span').textContent = space.dataset.breathStart;
      button.setAttribute('aria-pressed', 'false');
    };
    const toggle = () => {
      if (breathing) { stop(); return; }
      breathing = true;
      let outward = false;
      space.classList.add('is-breathing');
      phrase.textContent = space.dataset.breathIn;
      button.querySelector('span').textContent = space.dataset.breathStop;
      button.setAttribute('aria-pressed', 'true');
      timer = window.setInterval(() => {
        outward = !outward;
        phrase.textContent = outward ? space.dataset.breathOut : space.dataset.breathIn;
      }, 5000);
    };
    const visibility = () => { if (document.hidden) stop(); };
    range?.addEventListener('input', perspective);
    button?.addEventListener('click', toggle);
    document.addEventListener('visibilitychange', visibility);
    return () => {
      if (space && button && phrase) stop();
      range?.removeEventListener('input', perspective);
      button?.removeEventListener('click', toggle);
      document.removeEventListener('visibilitychange', visibility);
    };
  }

  function initialisePhotos(root) {
    const cards = [...root.querySelectorAll('[data-photo-card]')];
    const dialog = root.querySelector('[data-photo-dialog]');
    if (!cards.length || !dialog) return () => {};
    let view = 'studio';
    let active = 0;
    const padded = value => String(value).padStart(2, '0');
    const updateDialog = () => {
      const card = cards[active];
      const image = dialog.querySelector('[data-photo-full]');
      image.src = card.dataset[view];
      image.alt = card.dataset.product + ' — ' + card.dataset.label;
      dialog.querySelector('[data-photo-title]').textContent = card.dataset.product;
      dialog.querySelector('[data-photo-position]').textContent = root.dataset.position.replace('__label__', card.dataset.label).replace('__position__', String(active + 1)).replace('__total__', String(cards.length));
      dialog.querySelector('[data-photo-count]').textContent = padded(active + 1) + ' / ' + cards.length;
      dialog.querySelector('[data-photo-full-link]').href = card.dataset[view];
    };
    const changeView = next => {
      view = next === 'original' ? 'original' : 'studio';
      root.querySelectorAll('[data-photo-view]').forEach(button => button.setAttribute('aria-pressed', String(button.dataset.photoView === view)));
      cards.forEach(card => {
        const image = card.querySelector('img');
        const original = new URL(card.dataset[view], window.location.origin);
        const resized = width => { const url = new URL(original); url.searchParams.set('width', String(width)); return url.href; };
        image.srcset = [480, 800, 1254].map(width => resized(width) + ' ' + width + 'w').join(', ');
        image.src = resized(800);
        card.querySelector('[data-photo-view-label]').textContent = view === 'studio' ? root.dataset.studio : root.dataset.original;
      });
      if (dialog.open) updateDialog();
    };
    const open = index => {
      active = index;
      updateDialog();
      if (window.MSDialogs) window.MSDialogs.open(dialog);
      else dialog.showModal();
    };
    const move = step => { active = (active + step + cards.length) % cards.length; updateDialog(); };
    const click = event => {
      const viewButton = event.target.closest('[data-photo-view]');
      if (viewButton) { changeView(viewButton.dataset.photoView); return; }
      const card = event.target.closest('[data-photo-card]');
      if (card) { open(cards.indexOf(card)); return; }
      const navigation = event.target.closest('[data-photo-step]');
      if (navigation) move(Number(navigation.dataset.photoStep));
      if (event.target.closest('[data-ms-dialog-close]') && !window.MSDialogs) dialog.close();
    };
    const keyboard = event => {
      if (!dialog.open || !['ArrowLeft', 'ArrowRight'].includes(event.key)) return;
      event.preventDefault();
      move(event.key === 'ArrowLeft' ? -1 : 1);
    };
    root.addEventListener('click', click);
    dialog.addEventListener('keydown', keyboard);
    return () => {
      if (dialog.open) {
        if (window.MSDialogs) window.MSDialogs.close(dialog);
        else dialog.close();
      }
      root.removeEventListener('click', click);
      dialog.removeEventListener('keydown', keyboard);
    };
  }

  function init(scope = document) {
    const roots = [...scope.querySelectorAll('[data-ms-editorial]')];
    if (scope.matches?.('[data-ms-editorial]')) roots.unshift(scope);
    roots.forEach(root => {
      if (disposers.has(root)) return;
      const create = {mission: initialiseMission, mystery: initialiseMystery, photos: initialisePhotos}[root.dataset.msEditorial];
      if (create) disposers.set(root, create(root));
    });
  }
  window.MSEditorial = {init};
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => init(), {once: true});
  else init();
  document.addEventListener('shopify:section:load', event => init(event.target));
  document.addEventListener('shopify:section:unload', event => {
    event.target.querySelectorAll('[data-ms-editorial]').forEach(root => { disposers.get(root)?.(); disposers.delete(root); });
  });
})();
